<div class="apps-groups-page">
  @if (saveError) {
    <clr-alert [clrAlertType]="'danger'" [clrAlertAppLevel]="true" (clrAlertClosedChange)="saveError = undefined">
      <clr-alert-item>
        <span class="alert-text">{{ saveError }}</span>
      </clr-alert-item>
    </clr-alert>
  }

  <div class="page-body">
    <aside class="files-sidebar">
      <app-apps-groups-files-nav
        #filesNav
        [selectedFile]="selectedFile"
        (selectedFileChange)="onFileSelected($event)"
        (filesLoaded)="onFilesLoaded($event)">
      </app-apps-groups-files-nav>
    </aside>

    <section class="file-content">
      @if (pendingFile) {
        <clr-alert [clrAlertType]="'warning'" [clrAlertClosable]="false">
          <clr-alert-item>
            <span class="alert-text">
              You have unsaved changes in <strong>{{ selectedFile }}</strong>. Save them before opening <strong>{{ pendingFile }}</strong>?
            </span>
            <div class="alert-actions">
              <button type="button" class="btn alert-action" (click)="saveAndOpenPending()" [disabled]="!canSave">Save and open</button>
              <button type="button" class="btn alert-action" (click)="discardAndOpenPending()">Discard and open</button>
              <button type="button" class="btn alert-action" (click)="stayOnFile()">Stay here</button>
            </div>
          </clr-alert-item>
        </clr-alert>
      }

      <div class="file-header">
        <div class="file-heading">
          <div class="file-title-row">
            <h4 class="file-title">{{ selectedFile }}</h4>
            @if (selectedFileIsGenerated) {
              <span class="label label-info"><cds-icon shape="lock" size="sm"></cds-icon> read-only</span>
            }
            @if (isLoading) {
              <clr-spinner [clrInline]="true">Loading</clr-spinner>
            }
          </div>
          <p class="file-subtitle">{{ fileSubtitle }}</p>
        </div>

        <div class="file-actions">
          @if (editing?.dirty) {
            <span class="label label-warning">Unsaved changes</span>
            <button type="button" class="btn btn-sm btn-outline" (click)="discard()">
              <cds-icon shape="undo"></cds-icon> Discard
            </button>
          }
          @if (!selectedFileIsGenerated) {
            <button type="button" class="btn btn-sm btn-primary" (click)="save()" [disabled]="!canSave" [clrLoading]="isSaving">
              <cds-icon shape="floppy"></cds-icon> Save
            </button>
            <button type="button" class="btn btn-sm btn-danger-outline" (click)="confirmFileDeletion = true"
                    [disabled]="editing == undefined || isLoading">
              <cds-icon shape="trash"></cds-icon> Delete file
            </button>
          }
        </div>

        <ul class="nav" role="tablist" aria-label="File views">
          @for (tab of tabs; track tab.key) {
            <li role="presentation" class="nav-item">
              <button type="button" class="btn btn-link nav-link" role="tab" [class.active]="selectedTab == tab.key"
                      [attr.aria-selected]="selectedTab == tab.key" (click)="selectTab(tab.key)">
                {{ tab.label }}
                @if (tab.count() != undefined) {
                  <span class="badge">{{ tab.count() }}</span>
                }
              </button>
            </li>
          }
        </ul>
      </div>

      @if (failureMode && !isLoading) {
        <clr-alert [clrAlertType]="'danger'" [clrAlertClosable]="false">
          <clr-alert-item>
            <span class="alert-text">The apps groups of {{ selectedFile }} could not be loaded.</span>
          </clr-alert-item>
        </clr-alert>
      }

      <!-- The map stays in the page while another tab is shown: it keeps its zoom and its laid out graph -->
      <div class="tab-panel map-panel" role="tabpanel" [hidden]="selectedTab != 'visualisation'">
        <app-apps-groups-map
          #map
          [appsGroups]="appsGroups"
          [file]="selectedFile"
          [viewState]="viewState"
          (editAppsGroup)="onEditAppsGroup($event)"
          (editIncludedAppsGroup)="openGroup($event)">
        </app-apps-groups-map>
      </div>

      @if (selectedTab == 'groups' && editing) {
        <div class="tab-panel groups-panel" role="tabpanel">
          <section class="groups-list" aria-label="Groups of the file">
            <div class="groups-list-header">
              <h4>{{ editing.groups.length == 1 ? '1 group' : editing.groups.length + ' groups' }}</h4>
              @if (!editing.readOnly) {
                <button type="button" class="btn btn-sm btn-outline" (click)="newGroupOpen = true">
                  <cds-icon shape="plus"></cds-icon> New group
                </button>
              }
            </div>

            @if (editing.groups.length > 0) {
              <ul class="list-unstyled group-items">
                @for (item of groupItems; track item.group) {
                  <li [style.margin-left]="'calc(' + item.depth + ' * var(--cds-global-space-8))'" [class.nested]="item.depth > 0">
                    <button type="button" class="card clickable group-item" [class.selected]="item.group === selectedGroup"
                            [attr.aria-current]="item.group === selectedGroup ? 'true' : null" (click)="selectGroup(item.group)">
                      <span class="card-block">
                        <span class="group-item-top">
                          <span class="group-item-name">{{ item.group.name }}</span>
                          <span class="badge">{{ item.countLabel }}</span>
                        </span>
                        <span class="sources-bar" aria-hidden="true">
                          <span class="picked" [style.width]="item.pickedPercent"></span>
                          <span class="rule" [style.width]="item.rulePercent"></span>
                          <span class="included" [style.width]="item.includedPercent"></span>
                        </span>
                        <span class="group-item-recipe">{{ item.recipe }}</span>
                      </span>
                    </button>
                  </li>
                }
              </ul>
            } @else {
              <div class="empty-state">
                <cds-icon shape="node-group" size="xl"></cds-icon>
                <p class="empty-state-title">This file has no group yet</p>
                <p>A file is a set of groups you manage together, for an application, a team or a site.</p>
                @if (!editing.readOnly) {
                  <button type="button" class="btn btn-primary" (click)="newGroupOpen = true">Create the first group</button>
                }
              </div>
            }

            <div class="sources-legend">
              <span><span class="swatch picked"></span>Picked</span>
              <span><span class="swatch rule"></span>Name rules</span>
              <span><span class="swatch included"></span>Other groups</span>
            </div>
          </section>

          <div class="group-editor">
            @if (selectedGroup) {
              <app-apps-group-editor
                [editing]="editing"
                [group]="selectedGroup"
                [descriptionsCount]="descriptionsCountOf(selectedGroup)"
                [userFiles]="userFiles"
                [focusSection]="focusSection"
                (showOnMap)="showOnMap($event)"
                (seeFlows)="seeFlows($event)"
                (openGroup)="openGroup($event)"
                (duplicate)="duplicateGroup($event)"
                (deleted)="onGroupDeleted($event)">
              </app-apps-group-editor>
            } @else {
              <div class="empty-state">
                <cds-icon shape="cursor-hand-click" size="xl"></cds-icon>
                <p class="empty-state-title">Pick a group, or create one</p>
                <p>A group can be a fixed list of servers, everything named like <code>web-prod-*</code>, a whole application made of smaller groups, or all three.</p>
              </div>
            }
          </div>
        </div>
      }

      @if (selectedTab == 'flows') {
        <div class="tab-panel scroll-panel" role="tabpanel">
          @if (flowsFilterGroup) {
            <clr-alert [clrAlertType]="'info'" [clrAlertClosable]="false" [clrAlertLightweight]="true">
              <clr-alert-item>
                <span class="alert-text">Flows of <strong>{{ flowsFilterGroup }}</strong></span>
                <div class="alert-actions">
                  <button type="button" class="btn btn-sm btn-link" (click)="clearFlowsFilter()">Show all flows</button>
                </div>
              </clr-alert-item>
            </clr-alert>
          }
          <!-- The filters of the table are built from its flows: it waits for the map to have drawn them -->
          @if (flowsRepository) {
            <app-netscope-flows-datagrid
              [thirdPartyRefresh]="flowsRepository.refresh"
              [flowsRepository]="flowsRepository"
              [totalItemsCount]="flowsRepository.totalItemsCount"
              [flowDetails]="flowsRepository.dashboardFlows"
              [temporalParameter]="temporalParameter"
              [bidirectional]="true"
              [showHosts]="true"
              [switchFocus]="switchFocus">
            </app-netscope-flows-datagrid>
          } @else {
            <p class="flows-waiting"><clr-spinner [clrInline]="true"></clr-spinner> The flows show once the map has loaded them.</p>
          }
        </div>
      }
    </section>
  </div>
</div>

<app-new-apps-group-dialog
  [(open)]="newGroupOpen"
  [files]="userFiles"
  [defaultFile]="selectedFile"
  [nameError]="newGroupNameError"
  (create)="createGroup($event)">
</app-new-apps-group-dialog>

<clr-modal [(clrModalOpen)]="confirmFileDeletion" [clrModalSize]="'md'">
  <h3 class="modal-title">Delete the file {{ selectedFile }}?</h3>
  <div class="modal-body">
    <p>
      Its {{ editing?.groups.length == 1 ? 'group is' : (editing?.groups.length ?? 0) + ' groups are' }} deleted with it.
      @if (editing?.dirty) {
        Its unsaved changes are lost.
      }
    </p>
    @if (externalInclusionsCount > 0) {
      <p>
        {{ externalInclusionsCount }} {{ externalInclusionsCount == 1 ? 'group of another file includes' : 'groups of other files include' }}
        groups of this file and will point to missing groups.
      </p>
    }
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline" (click)="confirmFileDeletion = false">Keep it</button>
    <button type="button" class="btn btn-danger" (click)="deleteFile()" [clrLoading]="isDeletingFile">Delete the file</button>
  </div>
</clr-modal>
